import type { Service, ServiceOption } from './servicesStore';

export const hasStartingPrice = (service: Pick<Service, 'pricingMode' | 'options'>) =>
  service.pricingMode === 'starting-at' || Boolean(service.options?.length);

export const getServicePricePrefix = (service: Pick<Service, 'pricingMode' | 'options'>) =>
  hasStartingPrice(service) ? 'A partir de' : 'Investissement';

export const getServiceBillingLabel = (
  service: Pick<Service, 'billingType' | 'pricingNote' | 'workDays'>
) => {
  if (service.pricingNote?.trim()) {
    return service.pricingNote.trim();
  }

  if (service.billingType === 'monthly') {
    return 'Retainer mensuel';
  }

  if (service.billingType === 'days') {
    return service.workDays ? `${service.workDays} jours de travail` : 'Mission au forfait';
  }

  return 'Paiement unique';
};

export const getServiceOptionsSummary = (service: Pick<Service, 'options'>) => {
  const count = service.options?.length ?? 0;

  if (count <= 0) {
    return '';
  }

  return `${count} option${count > 1 ? 's' : ''} frequente${count > 1 ? 's' : ''}`;
};

export const getServicePrimaryCtaLabel = (service: Pick<Service, 'pricingMode' | 'options'>) =>
  hasStartingPrice(service) ? 'Voir base & options' : 'Commander ce service';

export const getServiceOptionKey = (serviceId: string, optionIndex: number) =>
  `${serviceId}::option::${optionIndex}`;

export const isSingleChoiceService = (
  service: Pick<Service, 'optionSelectionMode'>
) => service.optionSelectionMode === 'single';

export const getSelectedServiceOptions = (
  service: Pick<Service, 'id' | 'options'>,
  selectedOptionKeys: string[] = []
): ServiceOption[] => {
  if (!service.options?.length || !selectedOptionKeys.length) {
    return [];
  }

  const selectedKeys = new Set(selectedOptionKeys);

  return service.options.filter((_, optionIndex) =>
    selectedKeys.has(getServiceOptionKey(service.id, optionIndex))
  );
};

export const getServiceTotalPrice = (
  service: Pick<Service, 'id' | 'price' | 'options'>,
  selectedOptionKeys: string[] = []
) => {
  const selectedOptions = getSelectedServiceOptions(service, selectedOptionKeys);
  const replacingOption = selectedOptions.find(
    (option) => option.pricingMode === 'replace-total'
  );
  const baseAmount = replacingOption ? Number(replacingOption.price || 0) : Number(service.price || 0);
  const additiveOptions = selectedOptions.filter(
    (option) => option.pricingMode !== 'replace-total'
  );

  return Number(
    (
      baseAmount +
      additiveOptions.reduce((total, option) => total + Number(option.price || 0), 0)
    ).toFixed(2)
  );
};
